import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { DEPARTMENT_TREE_QUERY_KEY } from '@vlabs/monokit/entities/departments';
import { useCreateDepartment } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';

import type { CreateDepartmentFormValues } from './CreateDepartmentForm';
import { useCreateDepartmentTranslation } from './i18n';

interface UseCreateDepartmentSubmitParams {
  parentDepartmentId?: DepartmentId;
  onCreateDepartment?: (departmentId: DepartmentId) => void;
  onSuccess?: VoidFunction;
}

function useCreateDepartmentSubmit({
  parentDepartmentId = undefined,
  onCreateDepartment = undefined,
  onSuccess = undefined,
}: UseCreateDepartmentSubmitParams): (values: CreateDepartmentFormValues) => Promise<void> {
  const { t } = useCreateDepartmentTranslation();
  const queryClient = useQueryClient();
  const createDepartment = useCreateDepartment({
    mutation: {
      onSuccess: async ({ department_id: departmentId }) => {
        // после создания обновляем дерево и открываем новое подразделение
        await queryClient.invalidateQueries({ queryKey: DEPARTMENT_TREE_QUERY_KEY });
        toast.success(t('toast_success'));
        onCreateDepartment?.(departmentId);
        onSuccess?.();
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const handleSubmit = useCallback(async (values: CreateDepartmentFormValues): Promise<void> => {
    await createDepartment.mutateAsync({
      data: {
        ...values,
        // если parentDepartmentId не передан, создается подразделение первого уровня
        parent_department_id: parentDepartmentId,
      },
    });
  }, [createDepartment, parentDepartmentId]);

  return handleSubmit;
}

export { useCreateDepartmentSubmit };
export type { UseCreateDepartmentSubmitParams };
